import { useRef, type RefObject } from 'react';

import type { Chat } from '@mint-sdk/api-ai-assistant';

import { TextBlock } from './blocks/generic';
import { ChartBlock } from './blocks/generic/ChartBlock.jsx';
import { SystemErrorBlock } from './blocks/generic/SystemErrorBlock.jsx';
import { TableBlock } from './blocks/generic/TableBlock.jsx';
import type {
  ContentBlock,
  MessageBlock,
  MessageGraph,
  ConstructorTableProps,
  ConstructorGraphProps,
} from './types.js';

export const MessageConstructor = ({
  graphs,
  tables,
  chatId,
  messageId,
  isFullscreen,
  date,
  containerRef,
  isSystemError,
  messageGraph,
  type = 'agentic',
  tableComponent,
  textComponent,
  chartComponent,
}: {
  type: 'mobile' | 'agentic' | 'desktop';
  messageGraph: MessageGraph;
  chatId: string;
  messageId: string;
  graphs: Chat.AiChatMessageRecordType['message']['additionalMetadata']['graphs'];
  tables: Chat.AiChatMessageRecordType['message']['additionalMetadata']['tables'];
  date: string;
  isFullscreen: boolean;
  containerRef?: RefObject<HTMLDivElement | null>;
  tableComponent?: React.ComponentType<ConstructorTableProps>;
  textComponent?: React.ComponentType<React.ComponentProps<typeof TextBlock>>;
  chartComponent?: React.ComponentType<ConstructorGraphProps>;
  isSystemError?: boolean;
}) => {
  const { blocksMap, blocksOrder, relations } = messageGraph;
  const blockRefs = useRef(new Map<string, HTMLDivElement>());

  const blocks = blocksOrder
    .map((blockId) => blocksMap.get(blockId))
    .filter((block): block is MessageBlock => block !== undefined);

  const getRelatedBlocks = (blockId: string): ContentBlock[] => {
    const varNames = relations.outgoing.get(blockId);

    if (!varNames) return [];

    const relatedBlockIds = Array.from(varNames)
      .flatMap((varName) => Array.from(relations.incoming.get(varName) ?? [])) // all related blocks array (includes this block)
      .filter((id) => id !== blockId); // exclude this block

    return relatedBlockIds
      .map((id) => blocksMap.get(id))
      .filter((block): block is ContentBlock => block !== undefined);
  };

  const TextComponent = textComponent ?? TextBlock;

  if (isSystemError) return <SystemErrorBlock />;

  return (
    <>
      {blocks.map((block) => {
        const relatedBlocks = getRelatedBlocks(block.id);
        switch (block.type) {
          case 'text':
            return (
              <TextComponent
                key={`text-${block.id}`}
                text={block.text}
                isMobile={type === 'mobile'}
              />
            );

          case 'table':
            return (
              <TableBlock
                type={type}
                block={block}
                containerRef={containerRef}
                key={`${messageId}-${block.varName}-${block.id}`}
                dataframe={tables?.[block.varName] ?? []}
                chatId={chatId}
                messageId={messageId}
                relatedBlocks={relatedBlocks}
                blockRefs={blockRefs}
                tableComponent={tableComponent}
              />
            );

          case 'graph': {
            return (
              <ChartBlock
                key={`${messageId}-${block.varName}-${block.id}`}
                containerRef={containerRef}
                blockRefs={blockRefs}
                block={block}
                relatedBlocks={relatedBlocks}
                date={date}
                graph={graphs?.[block.varName] as Chat.Graph}
                dataframe={tables?.[block.varName] ?? []}
                isAgentic={isFullscreen}
                chartComponent={chartComponent}
              />
            );
          }
        }
      })}
    </>
  );
};
